'use client'

/**
 * UnifiedShareButton - Consistent share functionality across all tools
 * Handles native share API, fallback modal, and toast notifications
 */

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Share2, Copy, Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { analytics } from '@/lib/analytics'

interface UnifiedShareButtonProps {
  url: string
  title: string
  text: string
  /**
   * Visual style. Use `'hero-overlay'` for share buttons that sit on a colored
   * hero card — it renders the ghost button with white-on-color styling so
   * callers don't have to repeat the same className tail.
   */
  variant?: 'default' | 'outline' | 'ghost' | 'hero-overlay'
  size?: 'default' | 'sm' | 'lg'
  className?: string
  showIcon?: boolean
  /**
   * Calculator/tool identifier for analytics, e.g. `'lawn-size-calculator'`.
   * Emitted as `calculator_shared`'s `calculator_type`, matching the mobile
   * counterpart (`mobile/components/tools/shared/UnifiedShareButton.tsx`).
   * Omit and the component tracks nothing.
   */
  trackingId?: string
  onShareSuccess?: () => void
  onShareError?: (error: Error) => void
}

const HERO_OVERLAY_CLASSES = 'text-white hover:bg-white/20 border-white/30 border'

/**
 * Callers often pass a path ("/tools/...") from buildToolUrl. A pasted path is
 * useless to the recipient, so every share and copy path sends an absolute URL
 * resolved against the current origin. Absolute URLs pass through unchanged.
 */
export function resolveShareUrl(url: string): string {
  if (typeof window === 'undefined') return url
  try {
    return new URL(url, window.location.origin).toString()
  } catch {
    return url
  }
}

export function UnifiedShareButton({
  url,
  title,
  text,
  variant = 'outline',
  size = 'sm',
  className,
  showIcon = true,
  trackingId,
  onShareSuccess,
  onShareError,
}: UnifiedShareButtonProps) {
  const isHeroOverlay = variant === 'hero-overlay'
  const buttonVariant = isHeroOverlay ? 'ghost' : variant
  const [showFallbackModal, setShowFallbackModal] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleShare = async () => {
    const shareUrl = resolveShareUrl(url)
    // Try native share API first
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url: shareUrl,
        })
        // Native sheet completed (target picked / not aborted) — the OS routes
        // this to social apps, messaging, etc.
        if (trackingId) analytics.calculatorShared(trackingId, 'social')
        onShareSuccess?.()
        showToast('Shared successfully!', 'success')
      } catch (error) {
        // User cancelled share - don't show error
        if ((error as Error).name !== 'AbortError') {
          onShareError?.(error as Error)
          showToast('Failed to share', 'error')
        }
      }
    } else {
      // Fallback to modal with copy button
      setShowFallbackModal(true)
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(resolveShareUrl(url))
      setCopied(true)
      if (trackingId) analytics.calculatorShared(trackingId, 'link')
      showToast('Link copied to clipboard!', 'success')
      onShareSuccess?.()

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false)
        setShowFallbackModal(false)
      }, 2000)
    } catch (error) {
      onShareError?.(error as Error)
      showToast('Failed to copy link', 'error')
    }
  }

  return (
    <>
      {/* Share Button */}
      <Button
        variant={buttonVariant}
        size={size}
        onClick={handleShare}
        className={cn('min-h-[44px]', isHeroOverlay && HERO_OVERLAY_CLASSES, className)}
        aria-label="Share results"
      >
        {showIcon && <Share2 className="h-4 w-4 mr-2" aria-hidden="true" />}
        Share
      </Button>

      {/* Fallback Modal */}
      <Dialog open={showFallbackModal} onOpenChange={setShowFallbackModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Results</DialogTitle>
            <DialogDescription>
              Copy the link below to share your results
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center space-x-2">
            <div className="grid flex-1 gap-2">
              <Input
                readOnly
                value={resolveShareUrl(url)}
                className="text-sm"
                onClick={(e) => e.currentTarget.select()}
                aria-label="Share URL"
              />
            </div>
            <Button
              type="button"
              size="sm"
              className="px-3 min-h-[44px]"
              onClick={handleCopy}
              aria-label={copied ? 'Link copied' : 'Copy link'}
            >
              {copied ? (
                <Check className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Copy className="h-4 w-4" aria-hidden="true" />
              )}
            </Button>
          </div>

          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFallbackModal(false)}
              className="min-h-[44px]"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * Simple toast notification system
 * TODO: Replace with react-hot-toast or sonner for production
 */
function showToast(message: string, type: 'success' | 'error') {
  // Create toast container if it doesn't exist
  let container = document.getElementById('toast-container')
  if (!container) {
    container = document.createElement('div')
    container.id = 'toast-container'
    container.className = 'fixed bottom-4 right-4 z-50 flex flex-col gap-2'
    document.body.appendChild(container)
  }

  // Create toast element
  const toast = document.createElement('div')
  toast.className = cn(
    'flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg',
    'animate-in slide-in-from-bottom-5 duration-300',
    type === 'success' && 'bg-green-600 text-white',
    type === 'error' && 'bg-red-600 text-white'
  )

  const icon = document.createElement('span')
  icon.innerHTML = type === 'success'
    ? '<svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>'
    : '<svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>'

  const text = document.createElement('span')
  text.className = 'text-sm font-medium'
  text.textContent = message

  toast.appendChild(icon)
  toast.appendChild(text)
  container.appendChild(toast)

  // Remove toast after 3 seconds
  setTimeout(() => {
    toast.classList.add('animate-out', 'slide-out-to-bottom-5')
    setTimeout(() => {
      container?.removeChild(toast)

      // Remove container if empty
      if (container?.children.length === 0) {
        document.body.removeChild(container)
      }
    }, 300)
  }, 3000)
}
